/* Daily Dons — overlays.js
   Shared overlay engine: scroll lock, modal open/close with the original's
   220ms-enter / 150ms-exit transitions, fullscreen lightboxes with keyboard
   + swipe navigation, sticky mobile CTA bars, back-to-top, and the
   announcement bar cross-fade. All vanilla, all progressively enhanced. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var EXIT_MS = 150;

  /* ---- Scroll lock ---------------------------------------------------- */
  var lockCount = 0;
  function lockScroll() {
    lockCount++;
    document.documentElement.classList.add('dd-scroll-lock');
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove('dd-scroll-lock');
  }

  /* ---- Modal engine ----------------------------------------------------
     A modal is any [data-dd-modal] element. Openers: [data-modal-open="id"].
     Close: [data-modal-close], backdrop click, Escape. Focus moves to
     [data-modal-focus] (or the dialog) on open and returns on close. */
  var openModal = null;

  function show(el, opener) {
    if (openModal) hide(openModal.el, true);
    el.hidden = false;
    /* force a frame so the transition runs from the hidden state */
    void el.offsetWidth;
    el.classList.add('is-open');
    el.classList.remove('is-closing');
    lockScroll();
    openModal = { el: el, opener: opener || null };
    var focusEl = el.querySelector('[data-modal-focus]') || el;
    if (focusEl && focusEl.focus) focusEl.focus({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('dd:modal:open', { bubbles: true }));
  }

  function hide(el, instant) {
    el.classList.remove('is-open');
    el.classList.add('is-closing');
    var done = function () {
      el.hidden = true;
      el.classList.remove('is-closing');
    };
    if (instant || reduceMotion.matches) done();
    else setTimeout(done, EXIT_MS);
    unlockScroll();
    if (openModal && openModal.el === el) {
      var opener = openModal.opener;
      openModal = null;
      if (opener && opener.focus) opener.focus({ preventScroll: true });
    }
    el.dispatchEvent(new CustomEvent('dd:modal:close', { bubbles: true }));
  }

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-modal-open]');
    if (openBtn) {
      var target = document.getElementById(openBtn.getAttribute('data-modal-open'));
      if (target) {
        /* Pre-order modal grows out of the CTA that opened it. */
        var card = target.querySelector('.dd-modal__card[data-origin-scale]');
        if (card) {
          var r = openBtn.getBoundingClientRect();
          var x = r.left + r.width / 2, y = r.top + r.height / 2;
          card.style.transformOrigin =
            'calc(50% + ' + x + 'px - 50vw) calc(50% + ' + y + 'px - 50vh)';
        }
        show(target, openBtn);
        e.preventDefault();
      }
      return;
    }
    var closeBtn = e.target.closest('[data-modal-close]');
    if (closeBtn) {
      var modal = closeBtn.closest('[data-dd-modal]');
      if (modal) { hide(modal); e.preventDefault(); }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openModal) hide(openModal.el);
  });

  /* Keep keyboard focus inside the open dialog. */
  document.addEventListener('focusin', function (e) {
    if (!openModal) return;
    if (!openModal.el.contains(e.target)) {
      var focusEl = openModal.el.querySelector('[data-modal-focus]') || openModal.el;
      if (focusEl.focus) focusEl.focus({ preventScroll: true });
    }
  });

  /* ---- Lightbox media navigation --------------------------------------
     Lightboxes are modals whose [data-lightbox-items] children swap via
     [data-lightbox-prev/next] buttons, arrow keys and horizontal swipe. */
  function initLightbox(box) {
    var current = 0;
    var slides = [].slice.call(box.querySelectorAll('[data-lightbox-item]'));
    var counter = box.querySelector('[data-lightbox-counter]');
    if (!slides.length) return;

    function goTo(i) {
      current = (i + slides.length) % slides.length;
      slides.forEach(function (s, idx) {
        s.hidden = idx !== current;
        var video = s.querySelector('video');
        if (video) { if (idx === current) { video.play && video.play().catch(function(){}); } else video.pause(); }
      });
      if (counter) counter.textContent = (current + 1) + ' / ' + slides.length;
    }
    box.addEventListener('dd:modal:open', function () {
      var want = parseInt(box.getAttribute('data-lightbox-start') || '0', 10);
      goTo(isNaN(want) ? 0 : want);
    });
    box.addEventListener('dd:modal:close', function () {
      slides.forEach(function (s) { var v = s.querySelector('video'); if (v) v.pause(); });
    });
    box.addEventListener('click', function (e) {
      if (e.target.closest('[data-lightbox-prev]')) { goTo(current - 1); e.stopPropagation(); }
      else if (e.target.closest('[data-lightbox-next]')) { goTo(current + 1); e.stopPropagation(); }
      else if (!e.target.closest('[data-lightbox-stage] *')) hide(box);
    });
    document.addEventListener('keydown', function (e) {
      if (box.hidden) return;
      if (e.key === 'ArrowRight') goTo(current + 1);
      if (e.key === 'ArrowLeft') goTo(current - 1);
    });

    /* Swipe: horizontal = prev/next (multi-item), downward = dismiss. */
    var startX = 0, startY = 0, tracking = false;
    box.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      tracking = true;
      startX = e.touches[0].clientX; startY = e.touches[0].clientY;
    }, { passive: true });
    box.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) && slides.length > 1) {
        goTo(dx < 0 ? current + 1 : current - 1);
      } else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) {
        hide(box);
      }
    }, { passive: true });

    box.dispatchEvent(new CustomEvent('dd:lightbox:ready'));
    box.ddGoTo = goTo;
  }

  /* Openers that also choose the starting slide. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lightbox-open]');
    if (!btn) return;
    var box = document.getElementById(btn.getAttribute('data-lightbox-open'));
    if (!box) return;
    box.setAttribute('data-lightbox-start', btn.getAttribute('data-lightbox-index') || '0');
    show(box, btn);
    e.preventDefault();
  });

  /* ---- Sticky mobile CTA bars ----------------------------------------- */
  function initStickyBar(bar) {
    var armSelector = bar.getAttribute('data-arm-on');
    var hideSelector = bar.getAttribute('data-hide-when');
    var armed = false, hideInView = false, menuOpen = false;

    function update() {
      var visible = armed && !hideInView && !menuOpen;
      bar.style.transform = visible ? 'translate3d(0,0,0)' : 'translate3d(0,100%,0)';
      bar.style.pointerEvents = visible ? 'auto' : 'none';
      bar.setAttribute('aria-hidden', String(!visible));
      bar.querySelectorAll('a').forEach(function (a) { a.tabIndex = visible ? 0 : -1; });
    }

    var armTarget = armSelector && document.querySelector(armSelector);
    if (armTarget && 'IntersectionObserver' in window) {
      var io1 = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
          armed = true; update(); io1.disconnect();
        }
      }, { threshold: 0, rootMargin: '0px 0px -25% 0px' });
      io1.observe(armTarget);
    }
    var hideTarget = hideSelector && document.querySelector(hideSelector);
    if (hideTarget && 'IntersectionObserver' in window) {
      var io2 = new IntersectionObserver(function (entries) {
        hideInView = entries[0].isIntersecting; update();
      }, { rootMargin: '-12% 0px -12% 0px' });
      io2.observe(hideTarget);
    }
    window.addEventListener('dailydon:mobile-menu', function (e) {
      menuOpen = !!(e.detail && e.detail.open); update();
    });
    update();
  }

  /* ---- Back to top ----------------------------------------------------- */
  function initBackToTop(wrap) {
    var btn = wrap.querySelector('button');
    function onScroll() {
      var visible = window.scrollY > 600;
      wrap.style.opacity = visible ? '1' : '0';
      wrap.style.transform = visible ? 'translateY(0)' : 'translateY(8px)';
      wrap.style.pointerEvents = visible ? 'auto' : 'none';
      wrap.setAttribute('aria-hidden', String(!visible));
      if (btn) btn.tabIndex = visible ? 0 : -1;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    if (btn) btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    });
  }

  /* ---- Announcement bar cross-fade (5.5s hold / 250ms fade) ------------ */
  function initAnnouncement(bar) {
    var messages = [].slice.call(bar.querySelectorAll('[data-announcement-message]'));
    if (messages.length < 2) return;
    var index = 0;
    var text = bar.querySelector('[data-announcement-text]');
    setInterval(function () {
      var advance = function () {
        index = (index + 1) % messages.length;
        text.innerHTML = messages[index].innerHTML;
      };
      if (reduceMotion.matches) { advance(); return; }
      text.style.opacity = '0';
      setTimeout(function () { advance(); text.style.opacity = '1'; }, 250);
    }, 5500);
  }

  /* ---- Generic accordions ----------------------------------------------
     [data-accordion] container; each [data-accordion-item] has a trigger
     button[data-accordion-trigger] and a panel[data-accordion-panel]
     animated via grid-template-rows 0fr→1fr (the original FAQ pattern)
     or height (product details pattern uses the same reveal). */
  function initAccordion(container) {
    var exclusive = container.getAttribute('data-accordion') !== 'multi';
    container.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger || !container.contains(trigger)) return;
      var item = trigger.closest('[data-accordion-item]');
      var isOpen = item.classList.contains('is-open');
      if (exclusive) {
        container.querySelectorAll('[data-accordion-item].is-open').forEach(function (other) {
          if (other !== item) setOpen(other, false);
        });
      }
      setOpen(item, !isOpen);
    });
    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      var trigger = item.querySelector('[data-accordion-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', String(open));
    }
  }

  /* ---- Copy-link buttons (blog share bar) ------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    var url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () {
        btn.classList.add('is-copied');
        setTimeout(function () { btn.classList.remove('is-copied'); }, 1800);
      }).catch(function () {});
    }
  });

  function initAll(root) {
    root = root || document;
    root.querySelectorAll('[data-dd-lightbox]:not([data-lb-init])').forEach(function (el) {
      el.setAttribute('data-lb-init', '1'); initLightbox(el);
    });
    root.querySelectorAll('[data-sticky-cta]:not([data-sticky-init])').forEach(function (el) {
      el.setAttribute('data-sticky-init', '1'); initStickyBar(el);
    });
    root.querySelectorAll('[data-back-to-top]:not([data-btt-init])').forEach(function (el) {
      el.setAttribute('data-btt-init', '1'); initBackToTop(el);
    });
    root.querySelectorAll('[data-announcement-bar]:not([data-ab-init])').forEach(function (el) {
      el.setAttribute('data-ab-init', '1'); initAnnouncement(el);
    });
    root.querySelectorAll('[data-accordion]:not([data-acc-init])').forEach(function (el) {
      el.setAttribute('data-acc-init', '1'); initAccordion(el);
    });
  }

  document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.DDOverlays = {
    show: show,
    hide: hide,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    initAll: initAll
  };
})();
